import type { ICDFields } from "components/ICDCoding/icdAdapter";
import type { OptionType } from "components/inputs";
import type { NextLevelOfAppeal } from "pages/liens/LienTypes";

export type UserAccess = {
  can_view_section111: boolean;
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export type StateType =
  | "AL"
  | "AK"
  | "AZ"
  | "AR"
  | "CA"
  | "CO"
  | "CT"
  | "DE"
  | "FL"
  | "GA"
  | "HI"
  | "ID"
  | "IL"
  | "IN"
  | "IA"
  | "KS"
  | "KY"
  | "LA"
  | "ME"
  | "MD"
  | "MA"
  | "MI"
  | "MN"
  | "MS"
  | "MO"
  | "MT"
  | "NE"
  | "NV"
  | "NH"
  | "NJ"
  | "NM"
  | "NY"
  | "NC"
  | "ND"
  | "OH"
  | "OK"
  | "OR"
  | "PA"
  | "RI"
  | "SC"
  | "SD"
  | "TN"
  | "TX"
  | "UT"
  | "VT"
  | "VA"
  | "WA"
  | "WV"
  | "WI"
  | "WY";

export type TagType = {
  id: number;
  name: string;
};

export type NoteType = {
  id: number;
  text: string;
  file: number;
  service?: number | null;
  lien?: number | null;
  lien_file?: number | null;
  claim?: number | null;
  mentionee?: number | null;
  created_by: number;
  created_by_name?: string;
  organization: number;
  created: string;
};

export interface ClaimValidation {
  errors: ValidationDataType[];
  warnings: ValidationDataType[];
}

export type QRFLDPartCDEntityType = {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postal_code: string;
};

export type QRFLDPartCDType = {
  entity_loop_items: QRFLDPartCDEntityType[];
  part_type: string;
  benefit_contract_number: string;
  pbp_number: string;
  dtp1_start: string | null;
  dtp1_end: string | null;
  dtp2_start: string | null;
  dtp2_end: string | null;
  dtp3_start: string | null;
  dtp3_end: string | null;
  dtp4_start: string | null;
  dtp4_end: string | null;
  dtp5_start: string | null;
  dtp5_end: string | null;
  dtp6_start: string | null;
  dtp6_end: string | null;
  dtp7_start: string | null;
  dtp7_end: string | null;
  dtp8_start: string | null;
  dtp8_end: string | null;
  dtp9_start: string | null;
  dtp9_end: string | null;
  dtp10_start: string | null;
  dtp10_end: string | null;
  dtp11_start: string | null;
  dtp11_end: string | null;
  dtp12_start: string | null;
  dtp12_end: string | null;
  dtp13_start: string | null;
  dtp13_end: string | null;
  dtp14_start: string | null;
  dtp14_end: string | null;
};

export type QueryResponseFileLogDetailType = {
  id: number;
  created: string;
  modified: string;
  send_date: string;
  response_date: string;
  deleted_at: string | null;
  claim: number;
  rre: number;
  query_input_file_log: number;
  query_response_file_log: number;
  query_input_file_log_detail: number;
  hicn: string;
  last_name: string;
  first_initial: string;
  dob: string;
  sex_code: string;
  ssn: string;
  disposition_code: string;
  cms_document_control_number: string;
  rre_dcn_1: string;
  rre_dcn_2: string | null;
  part_a_entitlement_date: string | null;
  part_a_termination_date: string | null;
  part_b_entitlement_date: string | null;
  part_b_termination_date: string | null;
  part_c_items: QRFLDPartCDType[] | null;
  part_d_items: QRFLDPartCDType[] | null;
};

export type ClaimsDataType = ICDFields & {
  id: number;
  created: string;
  modified: string;
  last_query_send_date: string;
  last_claim_send_date: string;
  is_wcmsa_2025_04_04_fields_enabled: boolean;
  claimant_2_relationship: string;
  claimant_2_tin: string;
  claimant_2_last_name: string;
  claimant_2_first_name: string;
  claimant_2_middle_initial: string;
  claimant_2_organization_name: string;
  claimant_2_mailing_address_line1: string;
  claimant_2_mailing_address_line2: string;
  claimant_2_city: string;
  claimant_2_state: StateType;
  claimant_2_zipcode: string;
  claimant_2_zip_plus_4: string;
  claimant_2_phone: string;
  claimant_2_phone_extension: string;
  claimant_2_rep_indicator: string;
  claimant_2_rep_last_name: string;
  claimant_2_rep_first_name: string;
  claimant_2_rep_firm_name: string;
  claimant_2_rep_tin: string;
  claimant_2_rep_mailing_address_line1: string;
  claimant_2_rep_mailing_address_line2: string;
  claimant_2_rep_city: string;
  claimant_2_rep_state: StateType;
  claimant_2_rep_zipcode: string;
  claimant_2_rep_zip_plus_4: string;
  claimant_2_rep_phone: string;
  claimant_2_rep_phone_extension: string;
  claimant_3_relationship: string;
  claimant_3_tin: string;
  claimant_3_last_name: string;
  claimant_3_first_name: string;
  claimant_3_middle_initial: string;
  claimant_3_organization_name: string;
  claimant_3_mailing_address_line1: string;
  claimant_3_mailing_address_line2: string;
  claimant_3_city: string;
  claimant_3_state: StateType;
  claimant_3_zipcode: string;
  claimant_3_zip_plus_4: string;
  claimant_3_phone: string;
  claimant_3_phone_extension: string;
  claimant_3_rep_indicator: string;
  claimant_3_rep_last_name: string;
  claimant_3_rep_first_name: string;
  claimant_3_rep_firm_name: string;
  claimant_3_rep_tin: string;
  claimant_3_rep_mailing_address_line1: string;
  claimant_3_rep_mailing_address_line2: string;
  claimant_3_rep_city: string;
  claimant_3_rep_state: StateType;
  claimant_3_rep_zipcode: string;
  claimant_3_rep_zip_plus_4: string;
  claimant_3_rep_phone: string;
  claimant_3_rep_phone_extension: string;
  claimant_4_relationship: string;
  claimant_4_tin: string;
  claimant_4_last_name: string;
  claimant_4_first_name: string;
  claimant_4_middle_initial: string;
  claimant_4_organization_name: string;
  claimant_4_mailing_address_line1: string;
  claimant_4_mailing_address_line2: string;
  claimant_4_city: string;
  claimant_4_state: StateType;
  claimant_4_zipcode: string;
  claimant_4_zip_plus_4: string;
  claimant_4_phone: string;
  claimant_4_phone_extension: string;
  claimant_4_rep_indicator: string;
  claimant_4_rep_last_name: string;
  claimant_4_rep_first_name: string;
  claimant_4_rep_firm_name: string;
  claimant_4_rep_tin: string;
  claimant_4_rep_mailing_address_line1: string;
  claimant_4_rep_mailing_address_line2: string;
  claimant_4_rep_city: string;
  claimant_4_rep_state: StateType;
  claimant_4_rep_zipcode: string;
  claimant_4_rep_zip_plus_4: string;
  claimant_4_rep_phone: string;
  claimant_4_rep_phone_extension: string;
  tpoc_date_2: string;
  tpoc_amount_2: number;
  funding_delayed_beyond_tpoc_start_date_2: string;
  tpoc_date_3: string;
  tpoc_amount_3: number;
  funding_delayed_beyond_tpoc_start_date_3: string;
  tpoc_date_4: string;
  tpoc_amount_4: number;
  funding_delayed_beyond_tpoc_start_date_4: string;
  tpoc_date_5: string;
  tpoc_amount_5: number;
  funding_delayed_beyond_tpoc_start_date_5: string;
  plan_insurance_type: string;
  is_self_insured: boolean;
  self_insured_type: string;
  policyholder_last_name: string;
  policyholder_first_name: string;
  dba_name: string;
  legal_name: string;
  plan_contact_department_name: string;
  plan_contact_last_name: string;
  plan_contact_first_name: string;
  plan_contact_phone: string;
  plan_contact_phone_extension: string;
  no_fault_insurance_limit: number;
  cms_date_of_incident: string;
  industry_date_of_incident: string;
  state_of_venue: string;
  tin: string;
  office_code: number;
  policy_number: string;
  claim_number: string;
  exhaust_date_for_dollar_limit_for_no_fault_insurance: string;
  injured_party_representative_indicator: string;
  representative_last_name: string;
  representative_first_name: string;
  representative_firm_name: string;
  representative_tin: string;
  representative_mailing_address_line1: string;
  representative_mailing_address_line2: string;
  representative_city: string;
  representative_state: StateType;
  representative_zipcode: string;
  representative_zip_plus_4: string;
  representative_phone: string;
  representative_phone_extension: string;
  orm_indicator: boolean;
  orm_termination_date: string;
  tpoc_date_1: string;
  tpoc_amount_1: number;
  funding_delayed_beyond_tpoc_start_date_1: string;
  claimant_1_relationship: string;
  claimant_1_tin: string;
  claimant_1_last_name: string;
  claimant_1_first_name: string;
  claimant_1_middle_initial: string;
  claimant_1_organization_name: string;
  claimant_1_mailing_address_line1: string;
  claimant_1_mailing_address_line2: string;
  claimant_1_city: string;
  claimant_1_state: StateType;
  claimant_1_zipcode: string;
  claimant_1_zip_plus_4: string;
  claimant_1_phone: string;
  claimant_1_phone_extension: string;
  claimant_1_rep_indicator: string;
  claimant_1_rep_last_name: string;
  claimant_1_rep_first_name: string;
  claimant_1_rep_firm_name: string;
  claimant_1_rep_tin: string;
  claimant_1_rep_mailing_address_line1: string;
  claimant_1_rep_mailing_address_line2: string;
  claimant_1_rep_city: string;
  claimant_1_rep_state: StateType;
  claimant_1_rep_zipcode: string;
  claimant_1_rep_zip_plus_4: string;
  claimant_1_rep_phone: string;
  claimant_1_rep_phone_extension: string;
  wcmsa_2025_04_04_fields_enabled: boolean;
  msa_amount: number;
  msa_period: number;
  payout_indicator: string;
  initial_deposit_amount: number;
  anniversary_deposit_amount: number;
  case_control_number: string;
  professional_administrator_ein: number;
  injured_party_hicn: string;
  injured_party_ssn: string;
  injured_party_last_name: string;
  injured_party_first_name: string;
  injured_party_middle_initial: string;
  injured_party_gender: string;
  injured_party_dob: string;
  adjuster: string;
  icn: string;
  injured_party_full_name: string;
  is_deceased: boolean;
  orm_termination_date_too_far_out_on_last_save: boolean;
  tpoc_date_in_future_on_last_save: boolean;
  is_medicare_beneficiary: boolean;
  is_medicare_beneficiary_in_force: boolean;
  is_medicare_beneficiary_verbose: string;
  medicare_entitlement_date: string;
  is_medicare_entitlement_date_sourced_from_cms: boolean;
  is_reportable: boolean;
  next_scheduled_action: string;
  next_scheduled_action_verbose: string;
  last_updated: string;
  last_change_reason: string;
  cms_disposition: string;
  last_query_date: string;
  last_import_log_identifier: string;
  is_held: boolean;
  cms_found_duplicate: boolean;
  has_cms_errors_to_resolve: boolean;
  marked_for_cms_deletion: boolean;
  archived_on: string;
  can_archive_claim: boolean;
  truly_deleted_on: string;
  queried_after_death: boolean;
  exposure_continued_post_1980: boolean;
  report_to_cms_override: boolean;
  reportable_to_cms_override: boolean;
  imported_applied_error_code_1: string;
  imported_applied_error_code_2: string;
  imported_applied_error_code_3: string;
  imported_applied_error_code_4: string;
  imported_applied_error_code_5: string;
  imported_applied_error_code_6: string;
  imported_applied_error_code_7: string;
  imported_applied_error_code_8: string;
  imported_applied_error_code_9: string;
  imported_applied_error_code_10: string;
  imported_applied_compliance_flag_1: string;
  imported_applied_compliance_flag_2: string;
  imported_applied_compliance_flag_3: string;
  imported_applied_compliance_flag_4: string;
  imported_applied_compliance_flag_5: string;
  imported_applied_compliance_flag_6: string;
  imported_applied_compliance_flag_7: string;
  imported_applied_compliance_flag_8: string;
  imported_applied_compliance_flag_9: string;
  imported_applied_compliance_flag_10: string;
  rre: number;
  rre_name: string;
  rre_rre_id: number;
  predefined_policy: number;
  last_claim_input_file_log_detail: number;
  last_claim_response_file_log_detail: number;
  last_query_input_file_log_detail: number;
  last_query_response_file_log_detail: number;
  tags: TagType[];
  mitigation_notes: string;
};

export type ProductDataType = {
  id: number;
  type: string;
  organization: string;
};

export type ClaimsTableDataType = {
  id: number;
  icn: string;
  injured_party_full_name: string;
  is_medicare_beneficiary: boolean | null;
  is_medicare_beneficiary_verbose: string;
  next_scheduled_action_verbose: string;
  has_cms_errors_to_resolve: boolean;
  adjuster: string;
  tags: string;
  rre: number | undefined;
  rre_name?: string | undefined;
  client_name?: string | undefined;
};

export type TaskType = {
  file: number | null;
  assignee: number;
  message: string;
  due_date?: string;
  type: string;
  service: string | number | boolean;
};

export type TaskDataType = {
  id: number;
  assignee: UserType;
  due_date: null | string;
  claim_numbers: string[];
  dates_of_injury: string[];
  client_name: string;
  is_rush: boolean;
  is_24_hour_rush: boolean;
  is_3_day_rush: boolean;
  claimant_names: string[];
  claimant_birth_dates: (string | null)[];
  claimant_ssns: (string | null)[];
  status: "P" | "I" | "C";
  products: ProductDataType[];
  created: string;
  assigned_by: number;
  is_overdue: null | boolean;
  validations: TaskValidationType;
  action_url: string;
  message: string | null;
  service: {
    type: string | null;
    organization: number | null;
    id: number;
  };
  type: string;
  status_verbose: string;
  type_verbose: string;
  service_type_verbose: string;
  file: number;
  rre_id: number | null;
  rre_name: string | null;
  query_response_file_log_id: number | null;
  claim_response_file_log_id: number | null;
  query_response_file_log__date_retrieved: string | null;
  claim_response_file_log__date_retrieved: string | null;
};

export type TaskValidationType = {
  errors: Array<ValidationDetailType>;
};

export type BackgroundJobDataType = {
  id: number;
  job_type: string;
  // Raw, untranslated job_type value (e.g. "ocr_msa_development"), for code
  // that needs to match on the type itself rather than its display label.
  job_type_value?: string;
  status: string;
  status_message: string;
  file_id: number;
  file_url: string | null;
  msa_id: number;
  cost_projection_id: number;
  lien_file_id: number;
  lien_id: number;
  claim_id?: number | null;
  metadoc_id?: number | null;
  created: string;
  duration: number | null;
  started_by: string;
  minutes_in_progress: number;
  last_status_update_at: number | null;
  rre_name: string | null;
  bulk_id: string | null;
  // server-side gate for the retry action — true only for failed AI
  // sort/summary jobs, which are the only types that can be re-dispatched
  can_retry: boolean;
  review_payload?:
    | MSADecisionReviewPayload
    | MSADevelopmentReviewPayload
    | null;
  // only populated for OCR_MSA_DECISION jobs, since msa_id alone doesn't say which decision
  msa_decision_id?: number | null;
  msa_development_id?: number | null;
  // Set once the async submit task creates the Textract request, some time after the job itself is created.
  textract_request_id?: number | null;
  // The document this job is processing, from its own meta. Lets code find
  // "is this document mid-scan" before any service record links to it.
  document_id?: number | null;
};

export type ValidationDetailType = {
  validation_type: string;
  message: string;
  level: "ERROR" | "WARN";
  affects: string;
  field: string;
  verbose_name: string;
};

export type RREImportDataType = {
  id: number;
  rre: number;
  created: Date;
  modified: Date;
  import_document_s3_key: string | null;
  rejection_document_s3_key: string | null;
  status?: "P" | "I" | "C";
  imported_by: string | null;
  processing_started_on: Date | null;
  processing_duration: number | null;
  metadata: {
    new: number;
    update: number;
    delete: number;
    skip: number;
    error: number;
    invalid: number;
  };
  exception_message: string | null;
  antivirus_scan_status: string | null;
  antivirus_scan_status_verbose: string | null;
};

export type RREProfileType = {
  client_id: number;
  client_name: string;
  organization_id: number;
  rre_id: number;
  approved_for_claim_submission_by: string | null;
  approved_for_claim_submission_at: Date | null;
  approved_for_query_submission_by: string | null;
  approved_for_query_submission_at: Date | null;
  is_approved_for_claim_submission: boolean;
  is_approved_for_query_submission: boolean;
  is_approvable: boolean;
};

export type RREDataType = {
  id: number;
  created: Date;
  modified: Date;
  client_name?: string;
  rre_profile: RREProfileType;
  rre_id: number;
  name: string;
  street: string | null;
  city: string | null;
  state: StateType | null;
  zipcode: string | null;
  phone: string | null;
  is_fake: boolean;
  excluded_from_outbound_feeds: boolean;
  excluded_from_client_feeds: boolean;
  supports_client_feed_exclusion?: boolean;
  profile_report_date: Date | null;
  profile_changed_date: Date | null;
  naic: string | null;
  lines_of_business: OptionType[];
  reporter_type: string | null;
  query_input_send_day: number;
  query_file_communication_mode: string;
  claim_file_communication_mode: string;
  contact_name: string | null;
  contact_street: string | null;
  contact_city: string | null;
  contact_state: StateType | null;
  contact_zipcode: string | null;
  contact_phone: string | null;
  contact_title: string | null;
  contact_email: string | null;
  account_mgr_name: string | null;
  account_mgr_street: string | null;
  account_mgr_city: string | null;
  account_mgr_state: StateType | null;
  account_mgr_zipcode: string | null;
  account_mgr_phone: string | null;
  account_mgr_title: string | null;
  account_mgr_email: string | null;
  submission_agent_name: string | null;
  submission_agent_street: string | null;
  submission_agent_city: string | null;
  submission_agent_state: StateType | null;
  submission_agent_zipcode: string | null;
  submission_agent_phone: string | null;
  submission_agent_title: string | null;
  submission_agent_email: string | null;
  bcrc_edi_rep_name: string | null;
  bcrc_edi_rep_email: string | null;
  bcrc_edi_rep_phone: string | null;
  submission_timeframe_group: number | null;
  created_via_ocr: boolean;
};

export type PartABPlanCoverageTypeEnum = "A" | "B";
export type PartCDPlanCoverageTypeEnum = "C" | "D";

export type PartABPlanCoverageType = {
  id: number;
  historical: PartABPlanCoverageHistoricalType[];
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  effective_date: string | null;
  termination_date: string | null;
  type: PartABPlanCoverageTypeEnum;
  organization: number;
  msa: number;
  cost_projection: number | null;
  lien: number | null;
  created_by: number;
  claim_ids: number[];
};

export type PartCDPlanCoverageType = {
  id: number;
  historical: PartCDPlanCoverageHistoricalType[];
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  type: PartCDPlanCoverageTypeEnum;
  pbp_number: string | null;
  effective_date: string | null;
  termination_date: string | null;
  contract_name: string | null;
  contract_number: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  organization: number;
  msa: number;
  lien: number | null;
  cost_projection: number | null;
  created_by: number;
  claim_ids: number[];
};

export type PartABPlanCoverageHistoricalType = Omit<
  PartABPlanCoverageType,
  "historical"
>;
export type PartCDPlanCoverageHistoricalType = Omit<
  PartCDPlanCoverageType,
  "historical"
>;

export type PlanCoveragesType = {
  part_a_b_coverages: PartABPlanCoverageType[];
  part_c_d_coverages: PartCDPlanCoverageType[];
};

type TotalExposureAndMitigationType = {
  exposure_total: number;
  mitigation_total: number;
  exposure_mitigations_total: number;
};

type ServiceAssigneeType = {
  id: number;
  user_full_name: string;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  organization: number;
  user: number;
  service: number;
};

export const SERVICE_TYPE = {
  MSA: "Medicare Set-Aside",
  MCP: "MCP",
  LCP: "LCP",
  LIEN: "LIEN",
  AMENDED_REVIEW: "Amended Review",
  AR_SCREENING: "Amended Review Screening",
  MSA_111: "MSA-111",
  SECTION111: "111",
  PRE_MSA: "Pre-MSA Snapshot",
  DOCUMENT_MANAGEMENT: "Document Management",
  CONSULTING: "CON",
  AUDIT: "AUD",
  SECTION111_GENERAL: "S111G",
  SOFTWARE_LICENSE: "SWL",
  NTMSA: "Non-Threshold MSA",
} as const;

export type FMServiceType = (typeof SERVICE_TYPE)[keyof typeof SERVICE_TYPE];

// Extracted-but-unsaved OCR data for a CMS decision letter upload, stashed on BackgroundJob.meta until the user
// reviews and confirms it. Nothing here has been written to an MSADecision/MSADecisionLineItem yet.
export type MSADecisionReviewLetterLineItem = {
  name: string;
  price_per_service?: string | null;
  total_cost: string | null;
  // present on prescription items only
  ndc?: string;
  // Set by the backend's deterministic matching once it clears the confidence threshold; null means review by hand.
  matched_allocation_id?: number | null;
  match_type?: "ndc_exact" | "name_fuzzy" | null;
  confidence?: number | null;
};

export type MSADecisionReviewPayload = {
  // Always "matching_complete" once a reviewPayload exists at all, since everything here comes from one pass.
  stage: "matching_complete";
  // The letter isn't filed as a Document yet, since that only happens once the user confirms a decision type
  // and saves. Fields can individually be null when the letter's text didn't match a known label pattern.
  decision_fields: {
    case_control_number: string | null;
    claimant_name: string | null;
    medicare_id: string | null;
    date_of_injury: string | null;
    decision_type: string | null;
    decision_type_raw_text: string | null;
    amount_total: string | null;
    medical_total: string | null;
    rx_total: string | null;
    decision_date: string | null;
    cms_rationale: string | null;
  };
  treatment_line_items: MSADecisionReviewLetterLineItem[];
  prescription_line_items: MSADecisionReviewLetterLineItem[];
};

export type MSADevelopmentReviewPayload = {
  documents_requested: string | null;
  is_closeout: boolean;
};

export type MSADecisionType = {
  id: number;
  file: number;
  msa: number;
  re_review?: number | null;
  document?: number | null;
  decision_type?: string | null;
  decision_date?: string | null;
  date_received?: string | null;
  date_submitted?: string | null;

  line_items: MSADecisionLineItemType[];

  amount_total?: number | null;
  medical_total?: number | null;
  rx_total?: number | null;
  physical_therapy?: number | null;
  prescription_drug_a?: number | null;

  counter_amount_total?: number | null;
  counter_medical_total?: number | null;
  counter_rx_total?: number | null;
  counter_physical_therapy?: string | null;
  counter_prescription_drug_a?: number | null;

  counter_differential_total?: number | null;
  medical_differential_total?: number | null;
  rx_differential_total?: number | null;

  cms_rationale?: string;
  organization: number;
  decision_type_display?: string;
  created: string;
  modified: string;
  deletable: boolean;

  proposed_total_medical?: number | null;
  decision_total_medical?: number | null;
  proposed_total_prescription?: number | null;
  decision_total_prescription?: number | null;
};

export type MSAReReviewType = {
  id: number;
  msa: number;
  document?: number | null;
  entered_by: number;
  category: string | null;
  date_requested?: string | null;
  amount_of_change_requested?: number | null;
  description?: string | null;
  decision_date_received?: string | null;
  decision_approval_amount_total?: number | null;
  medical_total?: number | null;
  rx_total?: number | null;
  cms_rationale?: string | null;
  date_delivered_to_client?: string | null;
  total_proposed_amount?: number | null;
  proposed_wcmsa_amount?: number | null;
  proposed_rx_amount?: number | null;
  datetime_uploaded?: string | null;
  created?: string | null;
  modified?: string | null;
  counter_differential_total?: number | null;
  medical_differential_total?: number | null;
  rx_differential_total?: number | null;
  deletable: boolean;
};

export type MSADevelopmentType = {
  status_display: string;
  id: number;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  date_received: string | null;
  date_sent_to_client: string | null;
  date_received_from_client: string | null;
  documents_requested: string;
  notes: string;
  date_acknowledged: string | null;
  organization: number;
  msa: number;
  entered_by: number;
  acknowledged_by: number | null;
  is_closeout: boolean;
  document: number | null;
};

export type RatedAgeType = {
  id: number;
  msa: number | null;
  cost_projection: number | null;
  life_company: string;
  date_received: string;
  rated_age: number | null;
  unrated_life_expectancy: number | null;
  organization: number;
  median_rated_age: number | null;
  active_rated_age: number | null;
  rated_age_override: number | null;
  created: string;
  modified: string;
};

export interface MSADataType {
  id: number;
  plan_coverages: PlanCoveragesType;
  file: FileType;
  total_exposure_and_mitigation: TotalExposureAndMitigationType;
  total_exposure_and_mitigation_client_delivery: TotalExposureAndMitigationType;
  organization: number;
  client: ClientType;
  claimant: ClaimantType;
  section_111_claimant_url: string | null;
  employer: EmployerType | null;
  developments?: MSADevelopmentType[];
  workflow_claim: unknown | null; // coming soon
  service_assignee: ServiceAssigneeType;
  report_date: string | null;
  date_requested: string | null;
  date_approved: string | null;
  date_submitted: string | null;
  notice_of_submit_delivered_on: string | null;
  case_control_number: string | null;
  delivered_to_client_on: string | null;
  counter_differential_total: number | null;
  medical_differential_total: number | null;
  rx_differential_total: number | null;
  re_review_counter_differential_total: number | null;
  re_review_medical_differential_total: number | null;
  re_review_rx_differential_total: number | null;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  no_coverage: boolean;
  coverage_complete: boolean;
  query_medicare_id: string | null;
  query_claimant_name: string | null;
  query_gender: string | null;
  query_dob: string | null;
  claimant_chronological_age: number | null;
  claimant_rated_age: number | null;
  median_rated_age: number | null;
  active_rated_age: number | null;
  rated_age_override: number | null;
  claimant_life_expectancy: number | null;
  medical_total_proposed_amount: number | null;
  medical_proposed_wcmsa_amount: number | null;
  medical_proposed_rx_amount: number | null;
  medical_annuity_big_ticket: number | null;
  medical_seed_payment_amount: number | null;
  medical_annual_payment_amount: number | null;
  legal_total_proposed_amount: number | null;
  legal_proposed_wcmsa_amount: number | null;
  legal_proposed_rx_amount: number | null;
  legal_annuity_big_ticket: number | null;
  legal_seed_payment_amount: number | null;
  legal_annual_payment_amount: number | null;
  recent_medical_reviewed_on: string | null;
  most_recent_two_year_medicals_reviewed_from: string | null;
  most_recent_two_year_medicals_reviewed_to: string | null;
  payment_history_date: string | null;
  rx_history_date: string | null;
  date_posted: string | null;
  invoice_due_date: string | null;
  invoice_email_subject: string | null;
  invoice_description: string | null;
  invoice_total_amount_due: number | null;
  cloned_from_object_id: number | null;
  type: FMServiceType;
  type_code: string;
  accepted_conditions_notes: string;
  denied_conditions_notes: string;
  ai_allocator_notes: string;
  general_notes: string;
  warning_notes: string;
  potential_issues_notes: string;
  medication_notes: string;
  medication_history_notes: string;
  diagnostics_notes: string;
  uds_saliva_notes: string;
  medical_equipment_notes: string;
  surgeries_notes: string;
  injection_notes: string;
  therapy_notes: string;
  medical_legal_review_notes: string;
  medical_legal_notes: string;
  accident_description_notes: string;
  pre_existing_conditions_notes: string;
  legal_overview_notes: string;
  legal_exclusions_notes: string;
  legal_cost_exposure_warning_notes: string;
  legal_cost_containment_notes: string;
  final_settlement_documents_received_on: string | null;
  final_settlement_documents_uploaded_on: string | null;
  approval_or_counter_type: "A" | "C" | null;
  approval_or_counter_date_received: string | null;
  approval_or_counter_amount_total: number | null;
  approval_or_counter_medical_total: number | null;
  approval_or_counter_rx_total: number | null;
  approval_or_counter_cms_rationale: string;
  counter_acknowledged_on: string | null;
  re_review_date_requested: string | null;
  re_review_category: string | null;
  re_review_amount_of_change_requested: number | null;
  re_review_description: string;
  re_review_decision_date_received: string | null;
  re_review_decision_approval_amount_total: number | null;
  re_review_medical_total: number | null;
  re_review_rx_total: number | null;
  re_review_cms_rationale: string;
  re_review_date_delivered_to_client: string | null;
  re_review_total_proposed_amount: number | null;
  re_review_proposed_wcmsa_amount: number | null;
  re_review_proposed_rx_amount: number | null;
  re_review_datetime_uploaded: string | null;
  re_review_notes: string | null;
  re_review_approval_or_counter_datetime_uploaded: string | null;
  amended_review_date_of_assignment: string | null;
  amended_review_attempt_number: number | null;
  amended_review_overview_explanation: string;
  cloned_from_content_type: number | null;
  service: number;
  prior_msa: number | null;
  counter_acknowledged_by: number | null;
  approval_or_counter_datetime_uploaded: string | null;
  workspace_url: string;
  overview_url: string;
  payout_method: string;
  has_been_submitted_to_cms: boolean;
  jurisdiction: string | null;
  one_eleven_jurisdiction: string | null;
  re_review_available: boolean;
  decision_is_available: boolean;
  remaining_re_review_categories: string[];
  decisions: MSADecisionType[] | null;
  re_reviews: MSAReReviewType[] | null;
  msa_period: string | null;
  date_of_injury: DateOfInjury | null;
  total_exposure_savings: number | null;
  total_mitigation_savings: number | null;
  medical_review_is_complete: boolean;
  legal_review_is_complete: boolean;
  allocations_exist: boolean;
  is_professionally_admined: boolean;
  professional_admin_company_name: string | null;
  professional_admin_company_ein: string | null;
}

export type MSAWithValidationDataType = MSADataType & {
  validation: MSAValidation;
};

export type DateOfInjury = {
  id: number;
  file: number;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  description: string | null;
  accident_description: string | null;
  accepted_conditions: string | null;
  denied_conditions: string | null;
  description_of_claimants_job: string | null;
  defense_counsel: string;
  defense_counsel_email: string;
  defense_counsel_firm_name: string;
  claimant_counsel: string;
  attorney_name: string;
  attorney_address: string;
  attorney_phone: string;
  is_represented: boolean;
  jurisdiction: string | null;
  wcmsa_zip_code: string | null;
  one_eleven_jurisdiction: string | null;
  date: string;
  organization: number;
  lien_file: number | null;
};

export type ServiceDateOfInjury = ICDFields & {
  id: number;
  date_of_injury_id: number;
  date_of_injury: number;
  msa: number | null;
  cost_projection: number | null;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  date: string;
  description: string | null;
  accident_description: string | null;
  accepted_conditions: string | null;
  denied_conditions: string | null;
  description_of_claimants_job: string | null;
};

export type ClaimNumber = {
  id: number;
  number: string;
  organization: number;
  date_of_injury_id: number;
};

export type Status = {
  operations: "Red" | "Green" | string;
  review: "Red" | "Green" | string;
  client_analysis: "Red" | "Green" | string;
  invoice: "Red" | "Green" | string;
  query?: string;
  rated_age?: string;
  medical_review?: string;
  legal_review?: string;
  client_submission?: string;
  cms_submission?: string;
  cms_invoice?: string;
  cms?: string;
  final_settle_documents?: string;
};

export type AmendedReviewValidation = {
  errors: AmendedReviewValidationError[];
  status: Status;
};

export type AmendedReviewValidationError = {
  validation_type: null | string;
  message: string;
  level: "ERROR" | string;
  affects: "review" | "client_analysis" | "operations" | "invoice";
  field: string;
  verbose_name: string;
};

export type MSAStatus = {
  operations: string;
  query: string;
  "rated-age": string;
  "medical-review": string;
  "legal-review": string;
  invoice: string;
  "client-delivery": string;
  // absent for NTMSA / MSA-111 / Pre-MSA Snapshot, which are never submitted to CMS
  "cms-submission"?: string;
  "invoice-cms"?: string;
  "cms-status"?: string;
  fsd?: string;
};

export type LienStatus = {
  operations: string;
  query: string;
  "investigate-doi": string;
  analysis: string;
  invoice: string;
  "client-submission": string;
  appeal: string;
  "case-closure": string;
};

export type CostProjectionStatus = {
  operations: string;
  query: string;
  rated_age: string;
  medical_review: string;
  legal_review: string;
  invoice: string;
  client_submission: string;
};

export type AmendedReviewScreeningStatus = {
  operations: string;
  review: string;
  client_analysis: string;
  invoice: string;
};

export type MSAValidation = {
  errors: ValidationDataType[];
  status: MSAStatus | CostProjectionStatus | AmendedReviewScreeningStatus;
};

export type LienValidation = {
  errors: ValidationDataType[];
  status: LienStatus;
};

export interface InjuryAllocation {
  id: number;
  name: string;
  total_cost: number | null;
  is_included_exposure_or_mitigation: boolean;
  exposure_detail_type: "E" | "M";
  total_proposed_amount: number;
  total_exposure_and_mitigation: {
    exposure_total: number;
    mitigation_total: number;
    exposure_mitigations_total: number;
  };
  proposed_rx_amount: number;
  proposed_wcmsa_amount: number;
  number_of_years: number;
  organization: number;
  msa: number;
  cost_projection: number | null;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  is_excluded: boolean;
  has_exposure: boolean;
  has_mitigation: boolean;
  total_exposure: number;
  total_mitigation: number;
  exposure_included_for_client_submission: boolean;
  mitigation_included_for_client_submission: boolean;
  number_of_years_exposed: number;
  number_of_years_mitigated: number;
  exposed_unit_price: number;
  mitigated_unit_price: number;
  review_type: "LEGAL" | "MEDICAL";
}

export type TreatmentRow = InjuryAllocation & {
  position: number;
  units_per_year: number;
  annual_cost: number | null;
  exposed_annual_cost: number | null;
  mitigated_annual_cost: number | null;
  exposed_units_per_year: number | null;
  mitigated_units_per_year: number | null;
  is_included_in_seed_payment: boolean;
  non_medicare: boolean;
  surgical_pricing_calculation: number | null;
  total_exposure_savings: number | null;
  total_mitigation_savings: number | null;
  exposure_savings_realized: boolean;
  mitigation_savings_realized: boolean;
  cpt_codes: { code: string; price: string | number; description?: string }[];
  probability_of_success: number | null;
};

export type PrescriptionRow = InjuryAllocation & {
  ndc: string;
  dosage: string;
  position: number;
  units_per_month: number;
  units_per_year: number;
  unit_price: number;
  exposed_units_per_month: number;
  mitigated_units_per_month: number;
  exposed_units_per_year: number;
  mitigated_units_per_year: number;
  brand_name: string | null;
  active_ingredient: string | null;
  overridden: boolean;
  probability_of_success: number | null;
  exposed_dosage: string | null;
  mitigated_dosage: string | null;
  redbook_drug_product: number | null;
  total_exposure_savings: number | null;
  total_mitigation_savings: number | null;
  exposure_savings_realized: boolean;
  mitigation_savings_realized: boolean;
  formulation?: string;
  is_generic?: boolean;
  non_medicare?: boolean;
};

export type CMSTransmissionLogType = {
  id: number;
  rre_id: number;
  cause: string;
  item_count: number;
  triggered_by: number;
  triggered_by_name: string;
  trans_type: string;
  send_date: string;
  send_status: string;
  response_date: string;
  response_status: string;
  error_details: string | null;
  submitted_file_url: string | null;
  response_file_url: string | null;
};

export type ConstantTypes = {
  GENDERS: Array<{ value: string; label: string }>;
  BENEFICIARY_STATUS: Array<{ value: boolean | null; label: string }>;
  STATES: Array<{ value: string | null; label: string }>;
  US_STATES_OR_FC_OR_US_CHOICES: Array<{ value: string | null; label: string }>;
  ICD_INDICATORS: Array<{ value: string | null; label: string }>;
  RRE_REPORTER_TYPES: Array<{ value: string | null; label: string }>;
  SELF_INSURED_TYPE: Array<{ value: string | null; label: string }>;
  PLAN_INSURANCE_TYPE: Array<{ value: string | null; label: string }>;
  REPRESENTATIVE_TYPES: Array<{ value: string | null; label: string }>;
  RELATIONSHIPS: Array<{ value: string | null; label: string }>;
  EXTENDED_NEXT_SCHEDULED_ACTIONS: Array<{
    value: string | null;
    label: string;
  }>;
  DISPOSITION_CODES: Array<{ value: string | null; label: string }>;
  PAPERLESS_INDICATOR: Array<{ value: string | null; label: string }>;
  QUERY_INPUT_SEND_DAY: Array<{ value: number | null; label: string }>;
  SUBMISSION_TIME_FRAMES: Array<{ value: number | null; label: string }>;
  CMS_FILE_TYPES: Array<{ value: string | null; label: string }>;
  CMS_COMMUNICATION_MODE: Array<{ value: string | null; label: string }>;
  PAYOUT_INDICATOR: Array<{ value: string | null; label: string }>;
  IMPORT_TYPES: Array<{ value: string | null; label: string }>;
  TRUE_FALSE_CHOICES: Array<{ value: boolean | null; label: string }>;
  BACKGROUND_JOB_STATUS: Array<{ value: string | null; label: string }>;
  DOCUMENT_TYPES: Array<{ value: string | null; label: string }>;
  SUPPORTED_OPERATIONS_DOCUMENT_GENERATION_TYPES: string[];
  CMS_REVIEW_ONLY_DOCUMENT_GENERATION_TYPES: string[];
  AR_SCREENING_DOCUMENT_TYPES: Array<{ value: string | null; label: string }>;
  REPORT_TYPES: Array<{ value: string | null; label: string }>;
  REPORT_STATUS: Array<{ value: string | null; label: string }>;
  SERVICE_TYPE: Array<{ value: string | null; label: string }>;
  FILE_STATUS: Array<{ value: string | null; label: string }>;
  JURISDICTIONS: Array<{ value: string | null; label: string }>;
  SERVICE_STATUS: Array<{ value: string | null; label: string }>;
  PART_A_B_PLAN_COVERAGE_TYPES: Array<{ value: string | null; label: string }>;
  PART_C_D_PLAN_COVERAGE_TYPES: Array<{ value: string | null; label: string }>;
  CLIENT_TYPES: Array<{ value: string | null; label: string }>;
  CMS_MSA_DECISION_TYPE: Array<{ value: string | null; label: string }>;
  PAYOUT_METHOD_CHOICES: Array<{ value: string | null; label: string }>;
  MSA_RE_REVIEW_CATEGORY: Array<{ value: string | null; label: string }>;
  PAY_HISTORY_ITEM_STATUS: Array<{ value: string | null; label: string }>;
  MEDICAL_RECORD_STATUS: Array<{ value: string | null; label: string }>;
  MSA_DEVELOPMENT_STATUS: Array<{ value: string | null; label: string }>;
  ALLOCATION_TYPE: Array<{ value: string | null; label: string }>;
  TaskType: Array<{ value: string | null; label: string }>;
  ArgumentTypeChoices: Array<{ value: string | null; label: string }>;
  RequestToReopenDecision: Array<{ value: string | null; label: string }>;
  OfficeLevelChoices: Array<{ value: string | null; label: string }>;
  DEFAULT_INVOICE_PRICING_FIELDS: Array<{
    value: string | null;
    label: string;
  }>;
  INVOICE_PRICING_FIELD_GROUPS: Array<{ name: string; fields: string[] }>;
  INVOICE_TYPES: Array<{ value: string | null; label: string }>;
  USERS: Array<{ value: number; label: string; roles: string[] }>;
  OPERATIONS_USERS: Array<{ value: number | null; label: string }>;
  FUNGIBLE_MSA_TYPES: Array<{ value: string | null; label: string }>;
  CRCAppealResultChoices: Array<{ value: string | null; label: string }>;
  AppealResultChoices: Array<{ value: string | null; label: string }>;
  LIEN_APPEAL_LEVEL_ACTIONS: Array<{ value: string | null; label: string }>;
  FEATURE_FLAGS: Record<string, boolean>;
  PART_C_D_RESULT_TYPE: Array<{ value: string | null; label: string }>;
  SYSTEM_USER: { id: number };
  OPS_REPORT_TYPES: Array<{ value: string | null; label: string }>;
};

export type ValidationDataType = {
  level: string;
  field: string | null;
  verbose_name: string | null;
  message: string;
  affects?: string;
  validation_type?: string | null;
};

export type TinType = {
  id: number;
  created: string;
  modified: string;
  tin: string;
  office_code: number;
  tin_office_code_mailing_name: string;
  tin_office_code_mailing_address_line1: string;
  tin_office_code_mailing_address_line2: string;
  tin_office_code_city: string;
  tin_office_code_state: StateType;
  tin_office_code_zipcode: string;
  tin_office_code_zip_plus_4: string;
  tin_office_code_paperless_indicator: string;
  foreign_rre_address_line1: string;
  foreign_rre_address_line2: string;
  foreign_rre_address_line3: string;
  foreign_rre_address_line4: string;
  recovery_agent_mailing_name: string;
  recovery_agent_mailing_address_line1: string;
  recovery_agent_mailing_address_line2: string;
  recovery_agent_city: string;
  recovery_agent_state: StateType;
  recovery_agent_zipcode: string;
  recovery_agent_zip_plus_4: string;
  recovery_agent_tin: string;
  recovery_agent_paperless_indicator: string;
  has_cms_errors_to_resolve: boolean;
  rre: number;
  last_tin_input_file_log_detail?: string | null;
  last_tin_response_file_log_detail?: string | null;
};

export type PolicyType = {
  id: number;
  created: string;
  modified: string;
  plan_insurance_type: string | null;
  is_self_insured: boolean;
  self_insured_type: string | null;
  policyholder_last_name: string | null;
  policyholder_first_name: string | null;
  dba_name: string | null;
  legal_name: string | null;
  plan_contact_department_name: string | null;
  plan_contact_last_name: string | null;
  plan_contact_first_name: string | null;
  plan_contact_phone: string | null;
  plan_contact_phone_extension: string | null;
  no_fault_insurance_limit: number | null;
  name: string;
  policy_number: string;
  rre: number | null;
};

export type CustomService = {
  id: string;
  name: string;
  service: string;
};

export type ClientType = {
  id: number;
  name: string;
  client_id: string;
  email: string;
  billing_email: string | null;
  custom_bill_rate_adjustments: number | null;
  organization: number;
  custom_services: CustomService[];
  special_client_instructions: string | null;
  adjusters: AdjusterType[];
  trigger_lien_investigation: boolean;
  attach_consent_to_release_to_intake_ack: boolean;
  communications_instructions: string | null;
  type: string | null;
  owner: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  quickbooks_workspace?: number;
  invoice_has_rate_type: boolean;
  invoice_has_zero_tax: boolean;
  invoice_has_adjuster_name: boolean;
  invoice_has_state_of_loss: boolean;
  // DEFAULT_INVOICE_PRICING_FIELDS values archived on this client's fee
  // schedule. Only changed through the archive/restore endpoints. Required
  // here because the client endpoints always return it; the invoice
  // payload's client_archived_pricing_fields (InvoiceTypes.ts) is the
  // optional mirror of this list.
  archived_pricing_fields: string[];
  invoice_contact_name: string | null;
  invoicing_street: string | null;
  invoicing_street2: string | null;
  invoicing_city: string | null;
  invoicing_state: string | null;
  invoicing_zip_code: string | null;
  invoicing_country: string | null;
  conditional_payments: string | null;
  appeals_principal_vp: string | null;
  appeals_manager: string | null;
  appeals_analyst_specialist: string | null;
  release_settlement_language_review: string | null;
  social_security_verification_statement: string | null;
  medicare_query_investigation: string | null;
  expert_msp_consulting_services: string | null;
  annual_audit: string | null;
  msa_preparation_standard: string | null;
  msa_preparation_complex: string | null;
  pre_msa_snapshot: string | null;
  medical_cost_projection: string | null;
  life_care_plan: string | null;
  three_day_rush_preparation: string | null;
  twenty_four_hour_rush_preparation: string | null;
  cms_submission: string | null;
  cms_re_review: string | null;
  cms_amended_review_evaluation: string | null;
  cms_amended_review_msa: string | null;
  cms_amended_review_submission: string | null;
  traditional_medicare_investigation: string | null;
  medicare_advantage_investigation: string | null;
  conditional_payment_itemization: string | null;
  conditional_payment_dispute: string | null;
  recovery_agent_processing: string | null;
  rated_age: string | null;
};

export type EmployerType = {
  id: number;
  name: string;
  address: string;
  state: string;
  zipcode: string;
  created: Date | string;
  modified: Date | string;
  version: number;
  organization: number;
};

export type GenderType = "M" | "F" | "O";

export type ClaimantType = {
  id: number;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
  gender: GenderType;
  ssn: string;
  medicare_id: string | null;
  is_medicare_eligible: boolean | null;
  reasonable_expectation: boolean | null;
  birth_date: Date | string;
  address: string;
  city: string;
  state: StateType;
  zip_code: string;
  jurisdiction: string;
};

export type ClaimsTransmissionHistoryType = {
  id: string;
  date: string;
  type: string;
  is_historical: string;
  cms_disposition_code: string;
  applied_error_codes: string;
  applied_compliance_flags: string;
};

export interface CreateFileData {
  claimant_ids: string[];
  client_id: string;
  employer_id?: string;
  dates_of_injury: { date: string }[];
  services: { type: string }[];
  rre?: string;
  claimant: {
    first_name: string;
    last_name: string;
    birth_date: string;
    ssn?: string;
  };
  icn?: string;
  file?: File;
  insurance_types?: { type: string }[];
  due_date: string;
  claim_notes?: string;
}

export interface FileValidation {
  errors: ValidationDataType[];
  warnings: ValidationDataType[];
}

export interface ServiceType {
  id: number;
  label: string;
  type: string;
  type_verbose: string;
  workspace_url: string | null;
  value: number;
  organization?: number;
  status?: string;
  created: string;
  document_management_file?: number | null;
  costprojection?: number | null;
  msa?: number | null;
  amended_review_screening?: number | null;
  client_materials_fully_received_on?: string | null;
}

export const SERVICE_STATUS = {
  PENDING: "P",
  IN_PROGRESS: "IN",
  COMPLETED: "C",
} as const;

export interface FileAdjusterType {
  id: number;
  name: string;
  email: string;
  client_id: number;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  client: number;
  organization: number | null;
}

export interface ClaimAssociationType {
  organization: number;
  file: number;
  claim: number;
}
export interface FileType {
  id: number;
  adjuster: FileAdjusterType | null;
  client: ClientType & {
    organization: number;
    custom_services: CustomService[];
    special_client_instructions: string | null;
    adjusters: AdjusterType[];
  };
  employer: EmployerType | null;
  insurer: InsurerType | null;
  tpa: TPAType | null;
  claimants: ClaimantType[] | null;
  actions: {
    change_status: Array<string>;
  };
  claim_associations: ClaimAssociationType[];
  services: ServiceType[];
  dates_of_injury: DateOfInjury[];
  claim_numbers: ClaimNumber[];
  section_111_claimant_url: string | null;
  has_section_111_claim_linkage_available: boolean;
  accident_description: string;
  created: string;
  due_date: string | null;
  is_rush_verbose: string;
  jurisdiction: string | null;
  one_eleven_jurisdiction: string | null;
  insurance_types: {
    id: number;
    type: string;
    file: number;
    label: string;
    value: number;
  }[];
  status: string;
  status_verbose: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  date_referred: string | null;
  datetime_opened: string | null;
  datetime_closed: string | null;
  // set when this file was merged into another (the file is archived)
  merged_into: number | null;
  merged_at: string | null;
  // detail serializer only: archived files that were merged into this one
  merged_file_ids?: number[];
  is_rush: boolean;
  is_update_to_prior_msa: boolean;
  defense_counsel: string | null;
  is_represented: boolean;
  latest_qrfld_aggregation_checksum: string | null;
  // Liberty Mutual referral fields -- populated/editable only for Liberty
  // Mutual files (see isLibertyMutualFile in future-medical-utils.ts).
  grs_or_legacy_rcg: string;
  segmentation_type: string;
  rcg_carrier: string;
  referral_type: string;
  wcrc_case_id: string;
  claim_notes: string;
  // Manual-entry only, set in Operations (not intake) -- Col C of the Liberty
  // MSA Supplier Data Report.
  beneficiary_class: string;
  organization: number;
  // "OPERATIONS" | "INTAKE" (backend FILE_SOURCE)
  source: string;
  // human-readable FILE_SOURCE label -- "Internal" (keyed in by ops) |
  // "Online" (submitted through the client intake site)
  source_verbose: string;
  creator: number | { id: number; email: string } | null;
  owner: number;
  rre: number | null;
  is_24_hour_rush: boolean;
  is_3_day_rush: boolean;
  next_payment_date: string;
  next_total_amount_disputed: number;
  next_total_amount_to_pay: number;
  analysis_complete: boolean;
  case_closed: boolean;
  case_number: string;
  client_submission: boolean;
  cover_letter_version_number: number;
  cpl_date_issued: string | null;
  cpl_dispute_date: string | null;
  cpl_received: boolean;
  cpl_result: string | null;
  cpl_savings: number | null;
  cpn_amount: number | null;
  cpn_date_issued: string | null;
  cpn_dispute_date: string | null;
  cpn_due_date: string | null;
  cpn_received: boolean;
  cpn_result: string | null;
  cpn_savings: number | null;
  no_fault_exhaust_date: string | null;
  claimant_counsel: string | null;
  attorney_name: string | null;
  attorney_address: string | null;
  attorney_phone: string | null;
  defense_counsel_email: string | null;
  defense_counsel_firm_name: string | null;
  is_jones_act: boolean | null;
  is_longshore: boolean | null;
  awarded_ssd: boolean | null;
  applied_ssd: boolean | null;
  settlement_reached: boolean | null;
  estimated_settlement_amount: string | null;
  estimated_medical_settlement_value: string | null;
  reserve_settlement_amount: string | null;
  tentative_settlement_minimum: string | null;
  tentative_settlement_maximum: string | null;
  preferred_structure_settlement_broker: string | null;
  referring_party_name: string | null;
  referring_party_email: string | null;
  referring_party_phone: string | null;
  referral_acknowledgement_sent_at: string | null;
  adjuster_same_as_referring_party: boolean;
  next_level_of_appeal: NextLevelOfAppeal | null;
  total_savings: number | null;
  last_accessed?: string | null;
  coverage_complete: boolean;
  no_coverage: boolean;
}

export type FilesTableDataType = {
  id: number;
  client_name: string;
  employer_name: string;
  due_date: Date | string;
  datetime_closed: string | null;
  is_rush_verbose?: string;
  priority?: JSX.Element;
  status: string;
  services: string;
  claimant_name: string;
  date_of_birth: string | Date;
  dates_of_injury: string;
};

// Extended type for server-side sorting with backend field names as keys
export type ServerSortableFilesTableDataType = FilesTableDataType & {
  client__name: string;
  date_referred?: string;
  source?: string;
  employer__name: string;
  services__type: string;
  claimants__first_name: string;
  claimants__birth_date: string | Date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  is_rush?: any; // Can be JSX.Element for display or string for sorting
};

export type UserType = {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  has_legal_authority: boolean;
  has_management_authority: boolean;
  has_clinician_authority: boolean;
  has_cms_authority: boolean;
  has_audit_authority: boolean;
  has_operations_authority: boolean;
  has_finance_authority: boolean;
  has_future_medical_access: boolean;
  is_superuser: boolean;
  is_staff: boolean;
  user_type: string;
  has_intake_approval_authority: boolean;
  has_invoice_voiding_permissions: boolean;
  organization_id: number;
  active_licensed_services: OptionType[];
  has_ai_summary_authority: boolean;
  enabled_reports: string[];
};

export type AdjusterType = {
  id: number;
  name: string;
  email: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  client: number | null;
  organization: number | null;
  created: string;
  modified: string;
};

export type InsurerType = {
  id: number;
  name: string;
  address: string | null;
  state: string | null;
  zipcode: string | null;
  organization: number;
  created: string;
  modified: string;
};

export type TPAType = {
  id: number;
  name: string;
  address: string | null;
  state: string | null;
  zipcode: string | null;
  organization: number;
  created: string;
  modified: string;
};

// temp to get rid of errors
export type AnyType = Record<
  string,
  string | number | boolean | undefined | null
>;

export type MiscServiceType = {
  id: number;
  label: string;
  value: number;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  type: string;
  notes: string;
  organization: number;
  file: FileType;
  service: {
    type: string | null;
    organization: number | null;
    id: number;
  };
};

export interface AmendedReviewScreeningType {
  id: number;
  type: FMServiceType;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  date_received: string | null;
  due_date: string | null;
  date_sent_to_client: string | null;
  has_greater_than_10_percent_change: boolean | null;
  is_submitted_to_client: boolean | null;
  cms_approval_amount: number | null;
  explanation: string;
  section_111_claimant_url: string | null;
  claimant_chronological_age: number | null;
  claimant_rated_age: number | null;
  median_rated_age: number | null;
  active_rated_age: number | null;
  rated_age_override: number | null;
  claimant_life_expectancy: number | null;
  organization: number;
  file: FileType;
  msa: number | null;
  service: number;
  prior_amended_review_screening: number | null;
  overview_url: string;
  claimant: ClaimantType;
  is_professionally_admined: boolean;
  professional_admin_company_name: string | null;
  professional_admin_company_ein: string | null;
}

export type CostProjectionDataType = {
  id: number;
  plan_coverages: {
    part_a_b_coverages: PartABPlanCoverageType[];
    part_c_d_coverages: PartCDPlanCoverageType[];
  };
  client: ClientType;
  file: FileType;
  claimant: ClaimantType;
  section_111_claimant_url: string | null;
  type: "MCP" | "LCP";
  total_exposure_and_mitigation_client_delivery: TotalExposureAndMitigationType;
  organization: number;
  service: number;
  no_coverage: boolean;
  coverage_complete: boolean;
  document_version_number: number;
  workspace_url: string;
  overview_url: string;
  created: string;
  modified: string;
  deleted_at: string | null;
  version: number;
  cloned_from_object_id: number | null;
  cloned_from_content_type: number | null;
  payout_method: string;
  claimant_chronological_age: number | null;
  claimant_rated_age: number | null;
  median_rated_age: number | null;
  active_rated_age: number | null;
  rated_age_override: number | null;
  claimant_life_expectancy: number;
  medical_total_proposed_amount: number | null;
  medical_proposed_wcmsa_amount: number | null;
  medical_proposed_rx_amount: number | null;
  medical_annuity_big_ticket: number | null;
  medical_seed_payment_amount: number | null;
  medical_annual_payment_amount: number | null;
  legal_total_proposed_amount: number | null;
  legal_proposed_wcmsa_amount: number | null;
  legal_proposed_rx_amount: number | null;
  legal_annuity_big_ticket: number | null;
  legal_seed_payment_amount: number | null;
  legal_annual_payment_amount: number | null;
  recent_medical_reviewed_on: string | null;
  payment_history_date: string | null;
  rx_history_date: string | null;
  has_been_submitted_to_cms: boolean;
  delivered_to_client_on: string | null;
  date_of_injury: DateOfInjury | null;
  medical_review_is_complete: boolean;
  legal_review_is_complete: boolean;
  allocations_exist: boolean;
  is_professionally_admined: boolean;
  professional_admin_company_name: string | null;
  professional_admin_company_ein: string | null;
  uses_fma_document_type: boolean;
};

export type CostProjectionValidation = {
  errors: ValidationDataType[];
  warnings: ValidationDataType[];
  status: Status;
};

export type ICDCode = {
  code: string;
  icd_indicator: string;
  excluded_on_no_fault?: boolean | null;
  description: string;
};

export type GetICDCodesByInputArgs = {
  icd_indicator: string | null;
  code__istartswith?: string | null;
  description__icontains?: string | null;
  limit?: number;
};

export type ICDCodeResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ICDCode[];
};

export type PayHistoryRow = {
  id: number;
  status: string;
  date: string;
  amount: number;
  provider_name: string;
  description: string;
  date_of_service: string;
  date_of_service_end?: string | null;
  version: number;
  source_document_id: number | null;
  source_document?: {
    id: number;
    original_filename_as_uploaded?: string | null;
    original_filename?: string | null;
  } | null;
  date_of_injury_id?: number | null;
  date_of_injury?: {
    id: number;
    date?: string | null;
  } | null;
  claim_number_id?: number | null;
  claim_number?: {
    id: number;
    number?: string | null;
  } | null;
};

export type PayHistoryPayload = {
  id: number;
  status: string;
  date: string;
  amount: number;
  provider_name: string;
  description: string;
  date_of_service: string;
  date_of_service_end?: string | null;
  source_document_id: number | null;
};

export type RxPayHistoryRow = {
  id: number;
  status: string;
  date: string;
  amount: number;
  provider_name: string;
  description: string;
  date_of_service: string;
  version: number;
  pharmacy_name: string;
  rx_number: string;
  ndc: string;
  drug_name: string;
  prescriber_name: string;
  date_filled: string;
  date_paid: string;
  source_document_id: number | null;
  source_document?: {
    id: number;
    original_filename_as_uploaded?: string | null;
    original_filename?: string | null;
  } | null;
  date_of_injury_id?: number | null;
  date_of_injury?: {
    id: number;
    date?: string | null;
  } | null;
  claim_number_id?: number | null;
  claim_number?: {
    id: number;
    number?: string | null;
  } | null;
};

export type RxPayHistoryPayload = {
  id: number;
  status: string;
  date: string;
  amount: number;
  provider_name: string;
  description: string;
  date_of_service: string;
  version: number;
  pharmacy_name: string;
  rx_number: string;
  ndc: string;
  drug_name: string;
  prescriber_name: string;
  date_filled: string;
  date_paid: string;
  source_document_id: number | null;
};

export type MSAAllocation = {
  id: number;
  type: "proposed" | "decision";
  created: string;
  modified: string;
  deleted_at: null;
  version: number;
  name: string;
  number_of_years: number;
  total_cost: string;
  unit_price: string;
  units_per_year: number | null;
  annual_cost: string | null;
  organization: number;
  dosage: number;
  units_per_month: number;
};

export type MSADecisionLineItemType = {
  id: number;
  // Null for a decision-only line item (no proposal to compare against).
  total_percent_change: number | null;
  proposed_prescription_allocation: MSAAllocation;
  proposed_treatment_allocation: MSAAllocation;
  decision_prescription_allocation: MSAAllocation;
  decision_treatment_allocation: MSAAllocation;
  created: string;
  modified: string;
  deleted_at: null;
  version: number;
  organization: number;
  msa_decision: number;
};

export type ReportType = {
  id: number;
  report_type: string;
  status: string;
  modified: Date;
  created: Date;
  start_time: Date;
  end_time: Date;
  download_url: string;
};

export type SurgicalPricingCalculationType = {
  id: number;
  created: string;
  modified: string;
  deleted_at: string | null;
  organization: number;
  mmc_code: string;
  surgery_name: string;
  state: string;
  zip_code: string;
  procedure_type: "OUTPATIENT" | "INPATIENT";
  anesthesia_code: string;
  anesthesia_description: string;
  base_unit: number | null;
  total_units: number | null;
  conversion_factor: number | null;
  cpt_codes: { code: string; description: string; price: number }[];
  cpt_codes_description: { code: string; description: string }[];
  adj_apc_payment: number | null;
  facility_fee_apc_cpt: number | null;
  ms_drg_code: string;
  ms_drg_description: string;
  ms_drg_base_price: number | null;
  state_multiplier: number;
  anesthesia_price?: number | null;
  outpatient_price?: number | null;
  inpatient_price?: number | null;
  mmc: string;
};

export type RedbookDrugProduct = {
  id: number;
  ndc: string;
  product_name: string;
  active_ingredient: string;
  dea_class_code: string;
  product_category: string;
  dosage: string;
  is_generic: boolean;
  is_repackager: boolean;
  awp_package_price: string;
  awp_unit_price: string;
  formulation: string;
  mfg_name_long: string;
  form_code: string;
  strength_name: string;
  awp_p_price: string;
  awp_u_price: string;
  // Additional fields from API
  latest?: boolean;
  inactive?: boolean;
  record_change?: string;
  ndc_orig_config?: string;
  exceptional_drug?: string;
  solid_liquid?: string;
  measure_code?: string;
  product_size?: string;
  package_size?: string;
  package_qty_code?: string;
  thera_class_code?: string;
  gcr_code?: string;
  unit_dose_flag?: string;
  single_source_flag?: string;
  desi_drug_flag?: string;
  gfc_code?: string;
  anda_nda_flag?: string;
  awp_eff_date?: string;
  dp_eff_date?: string;
  dp_p_price?: string;
  dp_u_price?: string;
  ful_eff_date?: string;
  ful_p_price?: string;
  ful_u_price?: string;
  prev_ndc_disc_date?: string;
  prev_ndc_config?: string;
  prev_ndc?: string;
  roa_code?: string;
  master_form_code?: string;
};

export type DocumentManagementDataType = {
  id: number;
  file: FileType;
  service: ServiceType;
  ai_allocator_notes_version_number: number;
  ai_allocator_notes: string;
  workspace_url: string;
  overview_url: string;
  created: string;
  modified: string;
  organization: number;
  claimant: ClaimantType;
  date_of_injury: DateOfInjury | null;
  notes: string;
  type: "Document Management";
  accepted_conditions_notes: string;
  denied_conditions_notes: string;
  general_notes: string;
  medical_legal_notes: string;
  potential_issues_notes: string;
  medication_notes: string;
  therapy_notes: string;
};

export type CMPAlert = {
  item_key: string;
  item_type: string;
  days_active: number | string;
  description: string;
  penalty_amount: number;
  penalty_amount_with_inflation: number;
};

export type FeedImportLogDataType = {
  id: number;
  item_count: number | null;
  client_id: string;
  source_type: string;
  type: Date | string;
  type_verbose: string;
  status: string | null;
  status_verbose: string | null;
  created: string | Date;
  original_filename: string | null;
  processing_started_on: string | Date | null;
  processing_completed_on: string | Date | null;
  proxy_url: string | null;
  proxy_source_export_csv_url: string | null;
  progress: number | null;
};

export type FeedExportLogDataType = {
  id: number;
  client_id: string;
  type: Date | string;
  type_verbose: string;
  status: string | null;
  status_verbose: string | null;
  created: string | Date;
  modified: string | Date;
  export_start_time: string | Date | null;
  export_end_time: string | Date | null;
  error_message: string | null;
  response_item_description: string | null;
  rre_name: number | null;
};

export type InvoiceType = {
  id: number;
  created: string;
  modified: string;
  version: number;
  terms: string;
  date_posted: string | null;
  due_date: string | null;
  organization_id: number;
  service_id: number | null;
  quickbooks_workspace_id: number;
  file_id: number | null;
  status: string;
  quickbooks_id: string | null;
  deleted_at: string | null;
  client_id: number;
  description: string;
  email_subject: string;
  delivery_status: string;
  is_cms_invoice: boolean;
  invoice_type: string;
  deleted_by_id: number | null;
  billing_email_address: string | null;
  quickbooks_invoice_number: string | null;
  email_to_customer_when_finalized: boolean;
  created_by_id: number | null;
  finalized_by_id: number | null;
  last_updated_by_id: number | null;
  invoice_has_rate_type: boolean;
  // Related data
  client?: ClientType;
  service?: {
    id: number;
    type: string;
    type_verbose: string;
  };
};
